import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wand2, Check, X, Trash2, Plus, Share2, ChevronDown, ChevronRight, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { REL } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const KINDS = ["joins_on", "is_a", "derived_from", "belongs_to", "aggregates"];
const KIND_COLOR = {
  joins_on: "border-accent/50 text-accent bg-accent/10",
  is_a: "border-sky-500/40 text-sky-400 bg-sky-500/10",
  derived_from: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  belongs_to: "border-amber-500/40 text-amber-400 bg-amber-500/10",
  aggregates: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
};

function confColor(c) {
  if (c >= 0.85) return "bg-accent";
  if (c >= 0.75) return "bg-emerald-500";
  if (c >= 0.6) return "bg-amber-500";
  return "bg-muted-foreground";
}

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-border/60">
        <div className={`h-full ${confColor(value)}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-muted-foreground">{value}</span>
    </div>
  );
}

// Threshold above which the full graph becomes an unreadable hairball; we then
// require the user to focus on a table and only render its neighborhood.
const FOCUS_LIMIT = 18;

function LineageGraph({ nodes, edges, focusId, onFocus }) {
  const W = 760, H = 440, cx = W / 2, cy = H / 2, R = 165;
  const pos = useMemo(() => {
    const map = {};
    if (focusId) {
      map[focusId] = { x: cx, y: cy };
      const others = nodes.filter((n) => n.id !== focusId);
      others.forEach((n, i) => {
        const a = (2 * Math.PI * i) / Math.max(others.length, 1) - Math.PI / 2;
        map[n.id] = { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
      });
    } else {
      nodes.forEach((n, i) => {
        const a = (2 * Math.PI * i) / Math.max(nodes.length, 1) - Math.PI / 2;
        map[n.id] = { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
      });
    }
    return map;
  }, [nodes, focusId]);

  return (
    <svg data-testid={REL.graph} viewBox={`0 0 ${W} ${H}`} className="w-full rounded-xl border border-border/60 bg-card/30">
      {edges.map((e) => {
        const a = pos[e.from_fqn], b = pos[e.to_fqn];
        if (!a || !b) return null;
        // Bias the label toward the NON-focus endpoint so radiating edges don't
        // stack their labels near the centre.
        let lx = (a.x + b.x) / 2, ly = (a.y + b.y) / 2;
        if (focusId && e.from_fqn === focusId) { lx = a.x + (b.x - a.x) * 0.66; ly = a.y + (b.y - a.y) * 0.66; }
        else if (focusId && e.to_fqn === focusId) { lx = b.x + (a.x - b.x) * 0.66; ly = b.y + (a.y - b.y) * 0.66; }
        const label = e.from_column && e.to_column ? `${e.from_column} → ${e.to_column}` : e.kind;
        return (
          <g key={e.id}>
            <line
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={e.trusted ? "hsl(var(--accent))" : "hsl(var(--muted-foreground))"}
              strokeWidth={e.trusted ? 1.6 : 1}
              strokeDasharray={e.trusted ? "0" : "4 4"}
              opacity={e.status === "rejected" ? 0.15 : 0.7}
            />
            {focusId && (
              <text x={lx} y={ly - 3} textAnchor="middle" fontSize="7.5" fill="hsl(var(--muted-foreground))" className="font-mono">
                {label}{typeof e.confidence === "number" ? `  ·  ${e.confidence}` : ""}
              </text>
            )}
          </g>
        );
      })}
      {nodes.map((n) => {
        const p = pos[n.id];
        const isFocus = n.id === focusId;
        return (
          <g key={n.id} className="cursor-pointer" onClick={() => onFocus?.(n.id)}>
            <circle cx={p.x} cy={p.y} r={isFocus ? 9 : 6} fill={isFocus ? "hsl(var(--accent))" : "hsl(var(--primary))"} />
            <text x={p.x} y={p.y - (isFocus ? 14 : 10)} textAnchor="middle" fontSize={isFocus ? "10" : "9"}
                  fill="hsl(var(--foreground))" className="font-mono" fontWeight={isFocus ? 700 : 400}>
              {n.table}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function Relationships() {
  const { active } = useWorkspace();
  const [sources, setSources] = useState(null);
  const [sourceId, setSourceId] = useState(null);
  const [rels, setRels] = useState([]);
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [tables, setTables] = useState([]);
  const [inferring, setInferring] = useState(false);
  const [form, setForm] = useState({ from_fqn: "", from_column: "", to_fqn: "", to_column: "", kind: "joins_on" });
  const [expanded, setExpanded] = useState(null);
  const [pathForm, setPathForm] = useState({ from: "", to: "" });
  const [path, setPath] = useState(null);
  const [findingPath, setFindingPath] = useState(false);
  const [focus, setFocus] = useState("");

  const loadSources = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get("/sources", { params: { workspace_id: active.id } });
    setSources(data);
    // Prefer a successfully connected source over failed/half-connected ones.
    const healthy = data.find((s) => ["healthy", "connected"].includes(s.status));
    setSourceId((prev) => prev || healthy?.id || data[0]?.id || null);
  }, [active]);

  const loadAll = useCallback(async () => {
    if (!sourceId) return;
    const [r, g, s] = await Promise.all([
      api.get("/relationships", { params: { source_id: sourceId } }),
      api.get("/relationships/graph", { params: { source_id: sourceId } }),
      api.get("/schema", { params: { source_id: sourceId } }),
    ]);
    setRels(r.data);
    setGraph(g.data);
    setTables(s.data);
  }, [sourceId]);

  useEffect(() => { loadSources(); }, [loadSources]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const onInfer = async () => {
    setInferring(true);
    try {
      const { data } = await api.post("/relationships/infer", null, { params: { source_id: sourceId } });
      toast.success(`Inferred ${data.inferred} new edge${data.inferred === 1 ? "" : "s"}`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Inference failed");
    } finally {
      setInferring(false);
    }
  };

  const patchRel = async (id, body) => {
    await api.patch(`/relationships/${id}`, body);
    await loadAll();
  };

  const deleteRel = async (id) => {
    await api.delete(`/relationships/${id}`);
    await loadAll();
  };

  const onAdd = async (e) => {
    e.preventDefault();
    if (!form.from_fqn || !form.to_fqn) {
      toast.error("Pick a from and to table");
      return;
    }
    try {
      await api.post("/relationships", { source_id: sourceId, ...form });
      toast.success("Relationship added");
      setForm({ from_fqn: "", from_column: "", to_fqn: "", to_column: "", kind: "joins_on" });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const colsFor = (fqn) => tables.find((t) => t.table_fqn === fqn)?.columns || [];

  // Focused subgraph: the focused table + its direct neighbors only.
  const focusedView = useMemo(() => {
    if (!focus) return null;
    const liveEdges = graph.edges.filter((e) => e.status !== "rejected");
    const neighborIds = new Set([focus]);
    const subEdges = liveEdges.filter((e) => e.from_fqn === focus || e.to_fqn === focus);
    subEdges.forEach((e) => { neighborIds.add(e.from_fqn); neighborIds.add(e.to_fqn); });
    const subNodes = graph.nodes.filter((n) => neighborIds.has(n.id));
    return { nodes: subNodes, edges: subEdges };
  }, [focus, graph]);

  const tooManyNodes = graph.nodes.length > FOCUS_LIMIT;

  const findPath = async () => {
    if (!pathForm.from || !pathForm.to) {
      toast.error("Pick a start and end table");
      return;
    }
    setFindingPath(true);
    setPath(null);
    try {
      const { data } = await api.get("/relationships/path", {
        params: { source_id: sourceId, from_fqn: pathForm.from, to_fqn: pathForm.to },
      });
      setPath(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not compute path");
    } finally {
      setFindingPath(false);
    }
  };

  if (sources && sources.length === 0) {
    return (
      <div data-testid={REL.root} className="min-h-screen bg-background text-foreground">
        <WorkspaceNav />
        <main className="mx-auto w-full max-w-3xl px-6 py-20 text-center">
          <div data-testid={REL.empty} className="rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-16">
            <Share2 className="mx-auto h-8 w-8 text-accent" />
            <h2 className="mt-5 text-lg font-semibold">No source in this workspace</h2>
            <p className="mt-2 text-sm text-muted-foreground">Connect a source first, then infer relationships.</p>
            <Link to="/schema" className="mt-6 inline-block text-xs uppercase tracking-[0.18em] text-accent hover:underline">
              Go to Schema →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid={REL.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Relationship graph</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Joins and lineage across your schema. Inferred edges are trusted for query planning above 0.75 confidence.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {sources && sources.length > 1 && (
              <Select value={sourceId || ""} onValueChange={setSourceId}>
                <SelectTrigger data-testid={REL.sourceSelect} className="h-9 w-[220px] text-xs">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              onClick={onInfer}
              disabled={inferring || !sourceId}
              data-testid={REL.inferBtn}
              className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
            >
              {inferring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
              Infer relationships
            </Button>
          </div>
        </div>

        {graph.nodes.length > 0 && (
          <div className="mt-8">
            {/* Focus control — keeps large schemas readable */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Focus table</span>
              <input
                list="rel-focus-tables"
                data-testid={REL.focusSelect}
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                placeholder="Type a table to focus…"
                className="h-8 w-72 rounded-md border border-border/70 bg-card/60 px-2.5 text-xs text-foreground outline-none focus:border-accent"
              />
              <datalist id="rel-focus-tables">
                {graph.nodes.map((n) => <option key={n.id} value={n.id} />)}
              </datalist>
              {focus && (
                <Button variant="ghost" size="sm" data-testid={REL.focusClear} onClick={() => setFocus("")}
                  className="h-8 rounded-full text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Clear focus
                </Button>
              )}
            </div>

            {focus && focusedView ? (
              <>
                <LineageGraph nodes={focusedView.nodes} edges={focusedView.edges} focusId={focus} onFocus={setFocus} />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Showing <span className="text-accent">{focus}</span> and {focusedView.nodes.length - 1} directly related table(s). Click any node to re-focus. Edge labels show join keys + confidence.
                </p>
              </>
            ) : tooManyNodes ? (
              <div data-testid={REL.focusHint} className="rounded-xl border border-dashed border-border/60 bg-card/30 px-6 py-14 text-center">
                <Share2 className="mx-auto h-7 w-7 text-accent" />
                <p className="mt-4 text-sm font-medium">This schema has {graph.nodes.length} tables</p>
                <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                  Rendering them all at once is hard to read. Pick a <b>focus table</b> above to see just that table and its direct relationships, with join keys and confidence.
                </p>
              </div>
            ) : (
              <LineageGraph nodes={graph.nodes} edges={graph.edges} focusId={null} onFocus={setFocus} />
            )}
            <div className="mt-2 flex gap-4 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><span className="inline-block h-0.5 w-5 bg-accent" /> trusted (≥0.75)</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-0.5 w-5 border-t border-dashed border-muted-foreground" /> suggested</span>
            </div>
          </div>
        )}

        {/* ER path explorer */}
        {tables.length > 1 && (
          <div className="mt-8 rounded-xl border border-border/60 bg-card/40 p-4">
            <div className="flex items-center gap-2">
              <Route className="h-4 w-4 text-accent" />
              <h2 className="text-sm font-semibold">ER path explorer</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              See exactly how two tables connect and whether the join path is trusted for query planning.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]">
              <Select value={pathForm.from} onValueChange={(v) => setPathForm((f) => ({ ...f, from: v }))}>
                <SelectTrigger data-testid={REL.pathFrom} className="text-xs"><SelectValue placeholder="Start table" /></SelectTrigger>
                <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.table_fqn}>{t.table_fqn}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={pathForm.to} onValueChange={(v) => setPathForm((f) => ({ ...f, to: v }))}>
                <SelectTrigger data-testid={REL.pathTo} className="text-xs"><SelectValue placeholder="End table" /></SelectTrigger>
                <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.table_fqn}>{t.table_fqn}</SelectItem>)}</SelectContent>
              </Select>
              <Button
                onClick={findPath}
                disabled={findingPath}
                data-testid={REL.pathFind}
                className="rounded-md bg-foreground text-xs uppercase tracking-[0.14em] text-background hover:bg-foreground/90"
              >
                {findingPath ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Route className="mr-1 h-4 w-4" />}
                Find path
              </Button>
            </div>

            {path && !path.found && (
              <p data-testid={REL.pathNone} className="mt-4 rounded-lg border border-dashed border-border/60 px-3 py-4 text-center text-xs text-muted-foreground">
                No join path connects these tables yet. Infer or add a relationship to link them.
              </p>
            )}

            {path && path.found && (
              <div data-testid={REL.pathResult} className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {path.tables.map((t, i) => (
                    <span key={t} className="flex items-center gap-2">
                      <span className="rounded-md border border-border/60 bg-background px-2 py-1 font-medium">{t}</span>
                      {i < path.tables.length - 1 && <span className="text-muted-foreground">→</span>}
                    </span>
                  ))}
                  <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] uppercase ${path.trusted ? "bg-accent/15 text-accent" : "bg-amber-500/15 text-amber-400"}`}>
                    {path.trusted ? "trusted path" : `weakest hop ${path.min_confidence}`}
                  </span>
                </div>
                <ol className="space-y-1.5">
                  {path.hops.map((h, i) => (
                    <li key={i} data-testid={REL.pathHop} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/50 bg-background/60 px-3 py-2 text-[11px]">
                      <span className="font-mono text-muted-foreground">{i + 1}.</span>
                      <code className="text-accent">{h.join_expr || `${h.from_fqn} → ${h.to_fqn}`}</code>
                      <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] uppercase ${KIND_COLOR[h.kind] || ""}`}>{h.kind}</Badge>
                      <ConfidenceBar value={h.confidence} />
                      {!h.trusted && <span className="text-[10px] text-amber-400">below threshold</span>}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Manual add */}
        <form onSubmit={onAdd} className="mt-8 grid grid-cols-1 gap-3 rounded-xl border border-border/60 bg-card/40 p-4 md:grid-cols-6">
          <Select value={form.from_fqn} onValueChange={(v) => setForm((f) => ({ ...f, from_fqn: v, from_column: "" }))}>
            <SelectTrigger data-testid={REL.addFrom} className="text-xs"><SelectValue placeholder="From table" /></SelectTrigger>
            <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.table_fqn}>{t.table_fqn}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.from_column} onValueChange={(v) => setForm((f) => ({ ...f, from_column: v }))}>
            <SelectTrigger data-testid={REL.addFromCol} className="text-xs"><SelectValue placeholder="From column" /></SelectTrigger>
            <SelectContent>{colsFor(form.from_fqn).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
            <SelectTrigger data-testid={REL.addKind} className="text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.to_fqn} onValueChange={(v) => setForm((f) => ({ ...f, to_fqn: v, to_column: "" }))}>
            <SelectTrigger data-testid={REL.addTo} className="text-xs"><SelectValue placeholder="To table" /></SelectTrigger>
            <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.table_fqn}>{t.table_fqn}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.to_column} onValueChange={(v) => setForm((f) => ({ ...f, to_column: v }))}>
            <SelectTrigger data-testid={REL.addToCol} className="text-xs"><SelectValue placeholder="To column" /></SelectTrigger>
            <SelectContent>{colsFor(form.to_fqn).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="submit" data-testid={REL.addSubmit} className="rounded-md bg-foreground text-xs uppercase tracking-[0.14em] text-background hover:bg-foreground/90">
            <Plus className="mr-1 h-4 w-4" /> Add edge
          </Button>
        </form>

        {/* List */}
        <div className="mt-6 overflow-hidden rounded-xl border border-border/60">
          {rels.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No relationships yet. Click “Infer relationships” to detect foreign keys.
            </p>
          ) : (
            rels.map((r) => {
              const open = expanded === r.id;
              return (
                <div key={r.id} data-testid={REL.row} className="border-b border-border/40 last:border-0">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <button
                      type="button"
                      data-testid={REL.rowToggle}
                      onClick={() => setExpanded(open ? null : r.id)}
                      className="flex flex-1 flex-wrap items-center gap-2 text-left text-xs"
                    >
                      {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="font-medium">{r.from_fqn}<span className="text-muted-foreground">.{r.from_column}</span></span>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-medium">{r.to_fqn}<span className="text-muted-foreground">.{r.to_column}</span></span>
                      <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] uppercase ${KIND_COLOR[r.kind] || ""}`}>{r.kind}</Badge>
                      <ConfidenceBar value={r.confidence} />
                      {r.trusted ? (
                        <span className="text-[10px] text-accent">trusted</span>
                      ) : (
                        <span className="text-[10px] text-amber-400">below threshold</span>
                      )}
                      <span className="rounded border border-border/50 px-1.5 py-0 text-[10px] uppercase text-muted-foreground">{r.status}</span>
                    </button>
                    <div className="flex items-center gap-1">
                      {r.status !== "confirmed" && (
                        <Button variant="ghost" size="icon" data-testid={REL.confirmBtn} onClick={() => patchRel(r.id, { status: "confirmed" })} className="h-8 w-8 text-muted-foreground hover:text-accent" title="Confirm">
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      {r.status !== "rejected" && (
                        <Button variant="ghost" size="icon" data-testid={REL.rejectBtn} onClick={() => patchRel(r.id, { status: "rejected" })} className="h-8 w-8 text-muted-foreground hover:text-amber-400" title="Reject">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" data-testid={REL.deleteBtn} onClick={() => deleteRel(r.id)} className="h-8 w-8 text-muted-foreground hover:text-red-400" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {open && (
                    <div data-testid={REL.rationale} className="border-t border-border/30 bg-background/40 px-4 py-3 pl-10">
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {r.rationale}
                      </p>
                      {r.join_expr && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Join</span>
                          <code className="rounded bg-muted/40 px-2 py-0.5 text-[11px] text-accent">{r.join_expr}</code>
                        </div>
                      )}
                      {r.evidence && Object.keys(r.evidence).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {r.evidence.name_match && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">name matches table</span>}
                          {r.evidence.fk_column_in_target && <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] text-sky-400">key column in target</span>}
                          {r.evidence.target_has_id && <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">target has id PK</span>}
                          {!r.inferred && <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">manually defined</span>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
